<template>
  <div>
    <Spinner v-if="!isInitialized" />
    <div class="page-container" v-if="isInitialized" :class="{ small: maxmimizedUI }">
      <div class="editor-container">
        <CoverImage v-if="maxmimizedUI" :image="image" @close="maxmimizedUI = false" />
        <Editor
          v-bind="content"
          :theme="pageTheme"
          :maxmimizedUI="maxmimizedUI"
          @editor:update="editorUpdate"
          @editor:resize="onResize"
        />
      </div>
      <div class="settings">
        <div class="top-row">
          <v-btn @click="onPagePublish" text-color="white" :color="colors[page.page_publish]"
            >Sidan är {{ page.page_publish === 'online' ? 'online' : 'offline' }}</v-btn
          >
          <v-btn
            elevation="2"
            color="primary"
            class="mb-2"
            @click="save"
            :disabled="!hasUnsavedUpdates"
            :loading="isSaving"
            >Spara</v-btn
          >
          <CommonMenu
            :items="[
              { key: 'remove', value: 'Radera sidan' },
              { key: 'markAsGrunddata', value: 'Nåla fast sidan' },
              { key: 'markAsBubble', value: 'Visa som bubbla' },
            ]"
            @remove="onRemove"
            @markAsGrunddata="onMarkAsGrundData"
            @markAsBubble="onMarkAsBubble"
          />
        </div>
        <div class="preview-link">
          <PreviewLink :domain="this.page.page_section" :string="page.page_menu_title" />
        </div>
        <TemplatePageStandard
          v-bind="page"
          @update="update"
          @update-immediately="saveImmediately"
          class="my-3"
        >
          <template :slot="THEME_HU">
            <PartPageHavsutsikt v-if="section === 'havsutsikt'" v-bind="page" @update="update" />
          </template>
        </TemplatePageStandard>
        <h4>Taggar</h4>
        <v-chip-group v-model="pageTags" column multiple>
          <v-chip color="primary" v-for="(tag, i) in allTags" :key="i" filter outlined>
            {{ tag.tag }}
          </v-chip>
        </v-chip-group>
        <hr />
      </div>
      <Message ref="message" />
      <Confirm ref="confirm" />
    </div>
  </div>
</template>

<script>
/* eslint camelcase: 0 */

import { mapGetters, mapActions, mapMutations } from 'vuex'
import Editor from '@/components/shared/editor/Editor'
// import pageTemplates from '@/assets/data/pageTemplates'
import TemplatePageStandard from '@/components/templates/PageStandard'
import Message from '@/components/shared/Message'
// eslint-disable-next-line no-unused-vars
import { THEME_HAVET, THEME_LIVET, THEME_HU } from '../../assets/data/pageTemplates'
import PartPageHavsutsikt from '../../components/templates/PartPageHavsutsikt'
import Spinner from '@/components/shared/Spinner'
import Confirm from '@/components/shared/Confirm'
import { colors } from '@/assets/data/colors'
import CoverImage from '@/components/shared/CoverImage'
import CommonMenu from '@/components/shared/CommonMenu'
import { defaultPageMetaData } from '@/assets/data/metaData.js'
import PreviewLink from '@/components/shared/PreviewLink'

export default {
  name: 'Page',
  props: {
    id: {
      required: true,
    },
  },
  components: {
    Editor,
    TemplatePageStandard,
    Message,
    PartPageHavsutsikt,
    Spinner,
    Confirm,
    CoverImage,
    CommonMenu,
    PreviewLink,
  },
  data() {
    return {
      maxmimizedUI: false,
      colors,
      isInitialized: false,
      isSaving: false,
      updates: {},
      updatesContent: {},
      THEME_HU,
      THEME_LIVET,
      THEME_HAVET,
    }
  },
  async created() {
    this.mutTagsFilter({ key: 'pageSize', value: 9999 })
    await this.tagsReadCollection()
    await this.getSinglePage({ id: this.id })
    await this.getSingleContent({ type: 'page', id: this.id })
    this.isInitialized = true
  },
  computed: {
    ...mapGetters('tags', { getAllTags: 'getArray' }),
    ...mapGetters('pages', { getPageById: 'getById' }),
    ...mapGetters('content', { getContent: 'getContentByParentId' }),

    hasUnsavedUpdates() {
      return !!Object.keys({ ...this.updates, ...this.updatesContent }).length
    },

    page() {
      return { ...this.getPageById(this.pageId), ...this.updates }
    },
    image() {
      return `${process.env.VUE_APP_IMAGE_BACKEND}/870/${this.page.page_image}.jpg`
    },
    content() {
      return this.getContent(this.pageId)
    },
    section() {
      return this.$route.params.section
    },
    pageTags: {
      get() {
        return this.page.tags
          ? this.page.tags.map((t) => this.allTags.findIndex((at) => at.id === t.tag.id))
          : []
      },
      set(selectedTagsIndices) {
        const tags = selectedTagsIndices.map((i) => this.allTags[i]).map((t) => ({ tag: t }))
        this.update({ tags })
      },
    },
    allTags() {
      return [...this.getAllTags]
        .sort((a, b) => a.group - b.group)
        .sort((a, b) => a.tag.localeCompare(b.tag))
    },

    pageTheme() {
      switch (this.page.page_section) {
        case 'havet':
          return THEME_HAVET
        case 'livet':
          return THEME_LIVET
        case 'havsutsikt':
          return THEME_HU
        default:
          return THEME_HAVET
      }
    },
    pageId() {
      return Number(this.id)
    },
    pageMeta() {
      return this.page.page_meta ? JSON.parse(this.page.page_meta) : defaultPageMetaData
    },
  },
  methods: {
    ...mapActions('tags', { tagsReadCollection: 'readCollection' }),
    ...mapActions('pages', {
      getSinglePage: 'read',
      updatePage: 'update',
      deletePage: 'delete',
    }),
    ...mapActions('content', { getSingleContent: 'read', updateContent: 'update' }),
    ...mapMutations('tags', { mutTagsFilter: 'ADD_FILTER' }),

    onMarkAsGrundData() {
      const payload = { page_meta: JSON.stringify({ ...this.pageMeta, grunddata: true }) }
      this.saveImmediately(payload)
    },
    onMarkAsBubble() {
      const payload = { page_meta: JSON.stringify({ ...this.pageMeta, bubble: true }) }
      this.saveImmediately(payload)
    },
    async onRemove() {
      const shouldRemove = await this.$refs.confirm.open({
        title: 'Ta bort sidan',
        message: 'Är du säker på att du vill ta bort sidan?',
        saveButtonText: 'Ja',
        cancelButtonText: 'Avbryt',
      })
      if (!shouldRemove) return

      await this.deletePage({ ...this.getPageById(this.pageId) })
      this.$router.replace({ name: 'Pages' })
    },

    async saveImmediately(payload) {
      this.isSaving = true
      await this.updatePage({ ...this.page, ...payload })
      this.isSaving = false
    },
    async save() {
      try {
        if (!this.hasUnsavedUpdates) return
        this.isSaving = true
        await this.updatePage({ ...this.page, ...this.updates })
        if (Object.keys(this.updatesContent).length)
          await this.updateContent({ ...this.content, ...this.updatesContent })
        this.updates = {}
        this.updatesContent = {}
        this.$refs.message.open('Sidan sparad')
      } catch (err) {
        throw new Error(err)
      } finally {
        this.isSaving = false
      }
    },
    update(payload) {
      this.updates = { ...this.updates, ...payload }
    },

    editorUpdate(payload) {
      this.updatesContent = payload
    },
    async onPagePublish() {
      const newState = this.page.page_publish === 'online' ? 'offline' : 'online'
      await this.saveImmediately({ page_publish: newState })
    },
    onResize(val) {
      this.maxmimizedUI = val
    },
  },
  mounted() {
    this._keyListener = function (e) {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault() // present "Save Page" from getting triggered.
        this.save()
      }
    }
    document.addEventListener('keydown', this._keyListener.bind(this))
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._keyListener)
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedUpdates) {
      next()
      return
    }
    const shouldSave = await this.$refs.confirm.open({
      title: 'Osparade ändringar',
      message: 'Vill du spara innen du går vidare?',
      saveButtonText: 'Spara',
      cancelButtonText: 'Nej',
    })
    if (shouldSave) await this.save()

    next()
  },
}
</script>

<style lang="less" scoped>
.page-container {
  display: grid;
  grid-template-columns: auto 500px;
  gap: 15px;
  &.small {
    grid-template-columns: max(1100px);
    justify-content: center;
    & > div:nth-child(2) {
      visibility: hidden;
      display: none;
    }
  }
}
.settings {
  display: flex;
  flex-direction: column;
  gap: 5px;
  .top-row {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 15px;
  }
}
</style>
